/**
 * Pseudonymous page-view beacons for clickpath analysis.
 *
 * Each browser tab gets a random session id in `sessionStorage`. It survives
 * reloads and the SSO round-trip but is never linked to the signed-in user, so
 * the logs can show *paths through the site* without showing *who* took them.
 *
 * Navigation types:
 * - `landing`: the first page this tab showed. Because the router only mounts
 *   once a user is signed in, and SSO returns the user to the URL they asked
 *   for, this is the original deep link (e.g. /project-fair/2026), not the
 *   login page.
 * - `reload`: a later full page load in the same tab.
 * - `spa`: an in-app navigation.
 *
 * Telemetry must never break the page, so every failure is swallowed.
 */

const TAB_ID_STORAGE_ITEM = "clear.telemetry.tab";
const SEQ_STORAGE_ITEM = "clear.telemetry.seq";
const ENDPOINT = "/api/telemetry/pageview";

const getStore = () => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const newSessionId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

/** Coarse referrer class only — the referrer URL itself is never sent. */
export const classifyReferrer = (referrer = document.referrer) => {
  if (!referrer) return "none";
  try {
    const { host } = new URL(referrer);
    if (host === window.location.host) return "internal";
    if (/^login\.(microsoftonline|microsoft|live)\.com$/i.test(host)) return "sso";
    return "external";
  } catch {
    return "none";
  }
};

/**
 * Read (or mint) this tab's session and return the next sequence number.
 * `isNewSession` is true only for the very first page view in the tab.
 */
const nextSequence = () => {
  const store = getStore();
  let sid = store?.getItem(TAB_ID_STORAGE_ITEM);
  const isNewSession = !sid;
  if (!sid) {
    sid = newSessionId();
    store?.setItem(TAB_ID_STORAGE_ITEM, sid);
  }
  const seq = isNewSession ? 0 : Number(store?.getItem(SEQ_STORAGE_ITEM) || 0) + 1;
  store?.setItem(SEQ_STORAGE_ITEM, String(seq));
  return { sid, seq, isNewSession };
};

const send = (payload) => {
  const body = JSON.stringify(payload);
  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: "application/json" });
    if (navigator.sendBeacon(ENDPOINT, blob)) return;
  }
  if (typeof fetch === "function") {
    fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      credentials: "same-origin",
    }).catch(() => {});
  }
};

/**
 * Report one page view.
 * @param {{ pathname: string, hash?: string }} location
 * @param {boolean} isFirstInPageLoad true for the first view since the page loaded
 */
export const trackPageView = ({ pathname, hash }, isFirstInPageLoad) => {
  try {
    const { sid, seq, isNewSession } = nextSequence();
    const nav = isNewSession ? "landing" : isFirstInPageLoad ? "reload" : "spa";
    const payload = { sid, path: pathname, nav, seq };
    if (hash && hash !== "#") payload.hash = hash;
    if (nav !== "spa") payload.ref = classifyReferrer();
    send(payload);
  } catch {
    // Never let telemetry affect the page.
  }
};
